/**
 * 会话列表页（T06）可见部件元数据。
 *
 * 本文件是「安卓移植清单」的数据源之一：页面里每个可以看到的部件都在这里
 * 声明 id / 角色 / 中文名 / 390x844 逻辑坐标 / zIndex / props / notes，
 * 页面组件通过 `conversationsComponentMeta()` 取同一份元数据登记到全局注册表，
 * `scripts/generate-manifest.ts` 也在采集阶段直接引用本文件，
 * 保证「清单里的坐标」与「页面登记的坐标」不会各写一套。
 *
 * 坐标约定：与 App 外壳一致，一律是 390x844 画布上的绝对逻辑坐标
 * （x 向右、y 向下，左上角为原点），页面内容区从 y=24 的状态栏安全区之下开始。
 */
import type { ComponentMeta } from '../../core/types';
import { buildComponentMeta, type ComponentMetaInput } from '../../ui/meta';

/** 页面内容区顶部：画布 y=24（状态栏安全区之下）。 */
export const PAGE_TOP = 24;
/** 列表区起点（画布 y）。 */
export const LIST_TOP = 80;
/** 会话卡片行高（dp）。 */
export const ROW_HEIGHT = 72;

export const CONVERSATIONS_COMPONENTS: ComponentMetaInput[] = [
  {
    id: 'page.conversations.root',
    page: 'conversations',
    role: 'container',
    label: '会话列表页容器',
    layout: { x: 0, y: 24, width: 390, height: 744 },
    zIndex: 2,
    notes: '页面根容器（坐标含顶部 24dp 状态栏安全区偏移）；安卓用 Fragment 根 View',
  },
  {
    id: 'conversations.header',
    page: 'conversations',
    role: 'container',
    label: '会话列表页头',
    layout: { x: 0, y: 24, width: 390, height: 56 },
    zIndex: 3,
    notes: '标题 + 新建按钮容器；安卓用 MaterialToolbar',
  },
  {
    id: 'conversations.header.title',
    page: 'conversations',
    role: 'text',
    label: '页面标题：会话列表',
    layout: { x: 16, y: 36, width: 260, height: 32 },
    zIndex: 4,
    notes: '安卓用 Toolbar 标题（单行，超出省略）',
  },
  {
    id: 'conversations.newButton',
    page: 'conversations',
    role: 'button',
    label: '新建会话',
    layout: { x: 330, y: 32, width: 44, height: 44 },
    zIndex: 4,
    props: ['disabled'],
    notes: '安卓用 FloatingActionButton（mini）',
  },
  {
    id: 'conversations.list',
    page: 'conversations',
    role: 'list',
    label: '会话列表',
    layout: { x: 0, y: 80, width: 390, height: 688 },
    zIndex: 3,
    props: ['items'],
    notes: '安卓用 RecyclerView；卡片行高 72dp，第 n 行 y = 80 + 72n',
  },
  {
    id: 'conversations.item',
    page: 'conversations',
    role: 'container',
    label: '会话卡片（列表项模板）',
    layout: { x: 0, y: 80, width: 390, height: 72 },
    zIndex: 4,
    props: ['title', 'updatedAt', 'messageCount'],
    notes: '列表项模板；运行时按会话 id 登记为 conversations.item.<id>',
  },
  {
    id: 'conversations.item.title',
    page: 'conversations',
    role: 'text',
    label: '会话标题',
    layout: { x: 16, y: 92, width: 300, height: 24 },
    zIndex: 5,
    notes: '单行省略号',
  },
  {
    id: 'conversations.item.meta',
    page: 'conversations',
    role: 'text',
    label: '最后消息时间与消息数',
    layout: { x: 16, y: 120, width: 300, height: 18 },
    zIndex: 5,
    notes: '格式：最后消息时间 · N 条消息',
  },
  {
    id: 'conversations.empty',
    page: 'conversations',
    role: 'container',
    label: '会话列表空状态',
    layout: { x: 0, y: 300, width: 390, height: 140 },
    zIndex: 3,
    notes: '本地没有会话时显示',
  },
  {
    id: 'conversations.empty.title',
    page: 'conversations',
    role: 'text',
    label: '空状态主文案',
    layout: { x: 0, y: 316, width: 390, height: 32 },
    zIndex: 4,
    notes: '居中',
  },
  {
    id: 'conversations.empty.hint',
    page: 'conversations',
    role: 'text',
    label: '空状态说明',
    layout: { x: 0, y: 352, width: 390, height: 24 },
    zIndex: 4,
    notes: '居中，次要文本色',
  },
  {
    id: 'conversations.empty.actionButton',
    page: 'conversations',
    role: 'button',
    label: '新建第一个会话',
    layout: { x: 123, y: 392, width: 144, height: 44 },
    zIndex: 4,
    notes: '空状态主按钮，胶囊全圆角',
  },
  {
    id: 'conversations.deleteBackdrop',
    page: 'conversations',
    role: 'modal',
    label: '删除确认遮罩',
    layout: { x: 0, y: 24, width: 390, height: 744 },
    zIndex: 30,
    notes: '二次确认弹层遮罩；安卓用 Dialog 的 dim 层',
  },
  {
    id: 'conversations.deleteDialog',
    page: 'conversations',
    role: 'modal',
    label: '删除确认对话框',
    layout: { x: 45, y: 320, width: 300, height: 180 },
    zIndex: 31,
    notes: '长按 / 右键会话卡片后弹出；删除必须二次确认',
  },
  {
    id: 'conversations.deleteDialog.title',
    page: 'conversations',
    role: 'text',
    label: '删除确认标题',
    layout: { x: 69, y: 340, width: 252, height: 28 },
    zIndex: 32,
    notes: '安卓用 AlertDialog 标题',
  },
  {
    id: 'conversations.deleteDialog.message',
    page: 'conversations',
    role: 'text',
    label: '删除确认说明',
    layout: { x: 69, y: 372, width: 252, height: 48 },
    zIndex: 32,
    notes: '说明会话与消息一并删除',
  },
  {
    id: 'conversations.deleteDialog.cancelButton',
    page: 'conversations',
    role: 'button',
    label: '取消删除',
    layout: { x: 69, y: 436, width: 120, height: 44 },
    zIndex: 32,
    notes: '安卓用 AlertDialog negativeButton',
  },
  {
    id: 'conversations.deleteDialog.confirmButton',
    page: 'conversations',
    role: 'button',
    label: '确认删除',
    layout: { x: 201, y: 436, width: 120, height: 44 },
    zIndex: 32,
    notes: '危险色按钮',
  },
  {
    id: 'conversations.errorBanner',
    page: 'conversations',
    role: 'container',
    label: '错误提示条',
    layout: { x: 0, y: 660, width: 390, height: 64 },
    zIndex: 6,
    notes: '本地存储读写失败时显示',
  },
  {
    id: 'conversations.errorBanner.message',
    page: 'conversations',
    role: 'text',
    label: '错误信息',
    layout: { x: 16, y: 676, width: 358, height: 32 },
    zIndex: 7,
    notes: '错误色文案，最多两行',
  },
];

const COMPONENT_INDEX = new Map<string, ComponentMetaInput>(
  CONVERSATIONS_COMPONENTS.map((meta) => [meta.id, meta]),
);

/** 按 id 取声明式元数据输入；未登记直接抛错，避免清单静默缺项。 */
export function conversationsMeta(id: string): ComponentMetaInput {
  const found = COMPONENT_INDEX.get(id);
  if (found === undefined) throw new Error(`[conversations] 未登记的部件：${id}`);
  return found;
}

/** 按 id 取声明布局（页面内联定位与清单坐标共用一份数据）。 */
export function conversationsLayout(id: string): ComponentMetaInput['layout'] {
  return conversationsMeta(id).layout;
}

/** 按 id 构造完整 ComponentMeta，供 `useComponentMeta()` 登记。 */
export function conversationsComponentMeta(id: string): ComponentMeta {
  return buildComponentMeta(conversationsMeta(id));
}

/** 第 index 张会话卡片的声明布局（列表项模板；行高 72dp）。 */
export function conversationItemLayout(index: number): ComponentMetaInput['layout'] {
  return { x: 0, y: LIST_TOP + index * ROW_HEIGHT, width: 390, height: ROW_HEIGHT };
}
